import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, deleteUser } from '../services/apiUsers';
import UserModal from '../components/manager/UserModal';

const ROLE_META = {
    admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
    manager: { label: 'Manager', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    agent: { label: 'Vendedor', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
};

export default function UsersPage() {
    const qc = useQueryClient();
    const [modal, setModal] = useState({ open: false, user: null }); // user=null → create

    const { data: usersRaw, isLoading } = useQuery({
        queryKey: ['manager-users'],
        queryFn: getUsers,
    });
    const users = Array.isArray(usersRaw) ? usersRaw : (usersRaw?.data || []);

    const deactivateMut = useMutation({
        mutationFn: deleteUser,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['manager-users'] }),
    });

    const openCreate = () => setModal({ open: true, user: null });
    const openEdit = (u) => setModal({ open: true, user: u });
    const closeModal = () => setModal({ open: false, user: null });

    return (
        <div className="flex-1 overflow-auto">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-background-dark border-b border-gray-100 dark:border-gray-800 px-8 py-5">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">Gestión de Usuarios</h1>
                        <p className="text-sm text-text-secondary mt-0.5">{users.length} usuarios en tu organización</p>
                    </div>
                    <button
                        onClick={openCreate}
                        className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white font-bold rounded-lg shadow-lg shadow-primary/20 transition-all text-sm"
                    >
                        <span className="material-symbols-outlined text-[18px]">person_add</span>
                        Nuevo Usuario
                    </button>
                </div>
            </div>

            <div className="px-8 py-6">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
                    </div>
                ) : (
                    <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-800/60 text-xs font-bold uppercase tracking-wider text-text-secondary border-b border-gray-100 dark:border-gray-800">
                                    <th className="py-3 pl-6 pr-3 text-left">Usuario</th>
                                    <th className="py-3 px-3 text-left">Rol</th>
                                    <th className="py-3 px-3 text-left">Estado</th>
                                    <th className="py-3 px-3 text-right">Leads Activos</th>
                                    <th className="py-3 px-3 text-right">Conversión</th>
                                    <th className="py-3 pl-3 pr-6 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(Array.isArray(users) ? users : []).map(user => {
                                    const initials = user.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
                                    const rm = ROLE_META[user.role] ?? ROLE_META.agent;
                                    const loadPct = Math.min(100, Math.round((user.active_contacts / (user.max_leads || 50)) * 100));
                                    return (
                                        <tr key={user.id} className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors ${!user.is_active ? 'opacity-50' : ''}`}>
                                            <td className="py-4 pl-6 pr-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-primary/20 text-primary font-bold text-sm flex items-center justify-center shrink-0">
                                                        {initials}
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-sm">{user.name}</p>
                                                        <p className="text-xs text-text-secondary">{user.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-3">
                                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${rm.color}`}>{rm.label}</span>
                                            </td>
                                            <td className="py-4 px-3">
                                                {user.is_active
                                                    ? <span className="flex items-center gap-1 text-emerald-600 text-sm font-medium"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Activo</span>
                                                    : <span className="flex items-center gap-1 text-slate-400 text-sm"><span className="w-2 h-2 rounded-full bg-slate-400 inline-block" /> Inactivo</span>
                                                }
                                            </td>
                                            <td className="py-4 px-3 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <div className="h-1.5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full">
                                                        <div className={`h-1.5 rounded-full ${loadPct >= 80 ? 'bg-red-500' : loadPct >= 50 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${loadPct}%` }} />
                                                    </div>
                                                    <span className="text-sm font-semibold w-8 text-right">{user.active_contacts}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-3 text-right">
                                                <span className={`text-sm font-bold ${user.conversion_rate >= 30 ? 'text-emerald-500' : user.conversion_rate >= 15 ? 'text-amber-500' : 'text-slate-400'}`}>
                                                    {user.conversion_rate}%
                                                </span>
                                            </td>
                                            <td className="py-4 pl-3 pr-6">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => openEdit(user)}
                                                        className="p-1.5 text-text-secondary hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                                        title="Editar"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">edit</span>
                                                    </button>
                                                    {user.is_active && (
                                                        <button
                                                            onClick={() => deactivateMut.mutate(user.id)}
                                                            className="p-1.5 text-text-secondary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                            title="Desactivar"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">person_off</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {modal.open && (
                <UserModal
                    isOpen={modal.open}
                    onClose={closeModal}
                    user={modal.user}
                />
            )}
        </div>
    );
}
